if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 768px)');
        this.swiperEl = this.querySelector('.swiper');

        if (!this.elements.viewer) return;

        /* -----------------------------
          INIT SWIPER (MOBILE)
        ------------------------------ */
        if (window.innerWidth < 1024) {
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
              this.initSwiper();
            });
          } else {
            // DOMContentLoaded already fired (e.g. quick-add modal)
            requestAnimationFrame(() => this.initSwiper());
          }
        }

        /* -----------------------------
          ZOOM MODAL FIX (IMPORTANT)
        ------------------------------ */
        document.addEventListener('modalOpened', (event) => {
          if (
            event.detail?.modal?.classList.contains(
              'product-media-modal'
            )
          ) {
            this.onZoomModalOpen();
          }
        });

        /* -----------------------------
          SLIDE CHANGE
        ------------------------------ */
        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 500)
        );

        /* -----------------------------
          THUMBNAILS
        ------------------------------ */
        if (this.elements.thumbnails) {
          this.elements.thumbnails
            .querySelectorAll('[data-target]')
            .forEach((mediaToSwitch) => {
              const button = mediaToSwitch.querySelector('button');
              if (!button) return;

              button.addEventListener(
                'click',
                this.setActiveMedia.bind(
                  this,
                  mediaToSwitch.dataset.target,
                  false
                )
              );
            });
        }

        /* -----------------------------
          LOAD MORE IMAGES
        ------------------------------ */
        this.loadMoreButton = this.querySelector(
          '[load-more-media-button]'
        );

        if (this.loadMoreButton) {
          this.loadAllImages = this.loadAllImages.bind(this);
          this.loadMoreButton.addEventListener(
            'click',
            this.loadAllImages
          );
        }

        /* -----------------------------
          DESKTOP LAYOUT FIX
        ------------------------------ */
        if (
          this.dataset.desktopLayout?.includes('thumbnail') &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }
      }

      /* =============================
        SWIPER INIT
      ============================== */
      initSwiper() {
        if (!this.swiperEl) return;

        // Destroy existing instance
        if (this.swiperInstance) {
          this.swiperInstance.destroy(true, true);
          this.swiperInstance = null;
        }

        // Reset nav button visibility before re-evaluating
        const prevBtn = this.querySelector('.swiper-button-prev');
        const nextBtn = this.querySelector('.swiper-button-next');
        if (prevBtn) prevBtn.style.display = '';
        if (nextBtn) nextBtn.style.display = '';

        const slides = this.swiperEl.querySelectorAll('.swiper-slide');
        const hasMultipleSlides = slides.length > 1;

        if (!hasMultipleSlides) {
          if (prevBtn) prevBtn.style.display = 'none';
          if (nextBtn) nextBtn.style.display = 'none';
        }

        this.swiperInstance = new Swiper(this.swiperEl, {
          slidesPerView: 1,
          loop: hasMultipleSlides,
          watchSlidesProgress: true,
          simulateTouch: true,
          spaceBetween: 0,
          grabCursor: true,
          effect: 'slide', // Explicitly set effect
          pagination: {
            el: this.swiperEl.querySelector('.swiper-pagination'),
            clickable: true,
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          mousewheel: {
            forceToAxis: true,
            sensitivity: 1,
            thresholdDelta: 5, // Lower threshold for better response
            releaseOnEdges: true,
          },
          breakpoints: {
            768:{
              spaceBetween: 16,
            }
          },
          on: {
            slideChange: (swiper) => {
              const activeIndex = swiper.activeIndex;
              const currentElement = swiper.slides[activeIndex];
              this.onSlideChanged({ detail: { currentElement } });
            },
          },
          observer: true,
          observeParents: true,
        });
      }

      /* =============================
        ZOOM MODAL OPEN HANDLER
      ============================== */
      onZoomModalOpen() {
        if (!this.swiperEl) return;

        // Let modal finish layout
        setTimeout(() => {
          const activeIndex =
            this.swiperInstance?.activeIndex || 0;

          this.initSwiper(activeIndex);
          this.swiperInstance?.update();
          this.swiperInstance?.updateSize();
        }, 50);
      }

      /* =============================
        LOAD ALL IMAGES
      ============================== */
      loadAllImages(event) {
        if (event) event.preventDefault();

        const hiddenImages = this.querySelectorAll(
          '.product__media-item.hide-images'
        );

        hiddenImages.forEach((image) =>
          image.classList.remove('hide-images')
        );

        this.loadMoreButton &&
          (this.loadMoreButton.style.display = 'none');
      }

      /* =============================
        SLIDE CHANGED
      ============================== */
      onSlideChanged(event) {
        if (!this.elements.thumbnails) return;

        const thumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
          );

        this.setActiveThumbnail(thumbnail);
      }

      /* =============================
        SET ACTIVE MEDIA
      ============================== */
      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(
          `[data-media-id="${mediaId}"]`
        );

        if (!activeMedia) return;

        this.elements.viewer
          .querySelectorAll('[data-media-id]')
          .forEach((el) => el.classList.remove('is-active'));

        activeMedia.classList.add('is-active');

        this.preventStickyHeader();

        if (this.swiperInstance) {
          const slideIndex = [...this.elements.viewer.querySelectorAll('.swiper-slide')].indexOf(activeMedia);
          if (slideIndex > -1) {
            this.swiperInstance.slideTo(slideIndex);
          }
        } else {
          setTimeout(() => {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          });
        }

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );

        this.setActiveThumbnail(activeThumbnail);
      }

      /* =============================
        SET ACTIVE THUMBNAIL
      ============================== */
      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((btn) =>
            btn.removeAttribute('aria-current')
          );

        const button = thumbnail.querySelector('button');
        button?.setAttribute('aria-current', true);
      }

      /* =============================
        MEDIA PLAYBACK
      ============================== */
      playActiveMedia(activeItem) {
        window.pauseAllMedia?.();

        const deferredMedia =
          activeItem.querySelector('.deferred-media');

        deferredMedia?.loadContent(false);
      }

      /* =============================
        STICKY HEADER FIX
      ============================== */
      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader ||
          document.querySelector('sticky-header');

        this.stickyHeader?.dispatchEvent(
          new Event('preventHeaderReveal')
        );
      }

      /* =============================
        REMOVE LIST SEMANTIC
      ============================== */
      removeListSemantic() {
        if (!this.elements.viewer.slider) return;

        this.elements.viewer.slider.setAttribute(
          'role',
          'presentation'
        );

        this.elements.viewer.sliderItems?.forEach((slide) =>
          slide.setAttribute('role', 'presentation')
        );
      }
    }
  );
}
